import { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { Button } from 'antd';
import { AnimatePresence, motion } from 'framer-motion';

import Alert from 'components/Alert/Alert';
import DialogModal from 'components/DialogModal/DialogModal';
import HTMLText from 'components/HTMLText/HTMLText';
import InterviewScheduler from 'components/InterviewScheduler/InterviewScheduler';

import { selectActiveUserId } from 'features/applications/applicationsSelectors';
import applicationsService from 'features/applications/applicationsService';

import useErrorCatch from 'hooks/useErrorCatch';
import { useInterviewConfirmation } from 'hooks/useInterviewConfirmation';

import { GTMEventLogger } from 'utils/analytics';
import { convertSlotsTimeZone } from 'utils/dateUtils';

import { Application } from 'types/applications';
import {
  InterviewDetails,
  InterviewSlotSerialized
} from 'types/interviewSlots';

import { AlertType, DialogModalTypes, GTMEvents } from 'constants/enums';

import { standardAnimationProps } from '../../../constants/animations';
import SelectedSlot from '../../SelectedSlot/SelectedSlot';
import InterviewLinkForm from '../components/InterviewLinkForm/InterviewLinkForm';
import styles from './ConfirmInterview.module.scss';

interface ConfirmInterviewProps {
  interviewRequest: InterviewDetails;
  onConfirmed: () => void;
  onCanceled?: () => void;
  isLoading: boolean;
  onLoading: (isLoading: boolean) => void;
  application: Application | null;
  // TODO: remove once sidebar uses container-query layout instead of prop override
  isSidebarMode?: boolean;
}

const ConfirmInterview: FC<ConfirmInterviewProps> = ({
  interviewRequest,
  onConfirmed,
  onCanceled,
  isLoading,
  onLoading,
  application,
  isSidebarMode = false
}) => {
  const { t } = useTranslation();
  const catchError = useErrorCatch();
  const applicationId = useSelector(selectActiveUserId);
  const { jobseekerFullName } = application ?? {};

  const { form, selectedSlot, canConfirmSlot, isValidUrl, handleConfirmSlot } =
    useInterviewConfirmation(interviewRequest, onConfirmed, onLoading);

  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [proposedSlots, setProposedSlots] = useState<InterviewSlotSerialized[]>(
    []
  );

  useEffect(() => {
    if (interviewRequest?.slots?.slotList.length) {
      const candidateTimeZone = interviewRequest.slots.timeZone;
      const rawSlots = interviewRequest.slots.slotList;

      const convertedSlots = convertSlotsTimeZone(rawSlots, candidateTimeZone);

      setProposedSlots(convertedSlots);
    } else {
      setProposedSlots([]);
    }
  }, [interviewRequest]);

  const handleCancelInterview = async () => {
    if (!applicationId) {
      return;
    }

    try {
      onLoading(true);
      await applicationsService.cancelInterview(
        applicationId,
        interviewRequest.id
      );
      setIsCancelModalOpen(false);
      onCanceled?.();

      GTMEventLogger(GTMEvents.ApplicationsListViewSidebarInterviewCanceled);
    } catch (error) {
      catchError({ error });
    } finally {
      onLoading(false);
    }
  };

  return (
    <>
      <h2 className={styles.title}>
        {t('sidebar.application.interview.confirm.titleConfirm')}
      </h2>

      <Alert type={AlertType.Success} className={styles.slotsProvidedBanner}>
        <HTMLText
          text={t(
            'sidebar.application.interview.confirm.slotsProvidedMessage',
            {
              name: jobseekerFullName
            }
          )}
        />
      </Alert>

      <div className={styles.mainColumn}>
        <div className={styles.upperSection}>
          <InterviewLinkForm
            form={form}
            onValidateUrl={isValidUrl}
            disabled={isLoading}
          />
          <div className={styles.schedulerSection}>
            <InterviewScheduler
              proposedSlots={proposedSlots}
              forceMobileLayout={isSidebarMode}
            />
          </div>

          <AnimatePresence initial={false}>
            {selectedSlot ? (
              <motion.div
                key="confirm-interview-slot-section"
                className={styles.slotDetailsSection}
                {...standardAnimationProps}
              >
                <SelectedSlot slot={selectedSlot} />
              </motion.div>
            ) : null}
          </AnimatePresence>
        </div>

        <div className={styles.footerActions}>
          <Button
            danger
            type="primary"
            className={styles.footerBtnCancel}
            onClick={() => setIsCancelModalOpen(true)}
            disabled={isLoading}
          >
            {t('sidebar.application.interview.awaitingResponse.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleConfirmSlot}
            className={styles.footerBtnConfirm}
            disabled={!canConfirmSlot}
            loading={isLoading}
          >
            {t('sidebar.application.interview.confirm.titleConfirm')}
          </Button>
        </div>
      </div>

      <DialogModal
        type={DialogModalTypes.DangerConfirm}
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleCancelInterview}
        title={t('sidebar.application.interview.cancel.title')}
        description={t('sidebar.application.interview.cancel.areYouSure')}
        confirmLoading={isLoading}
      />
    </>
  );
};

export default ConfirmInterview;
